// Work Orders service for Turn Connect API — thin compatibility shim.
//
// The implementation lives in @turn-connect/shared (WorkOrdersService),
// shared with mobile. This module wires that service to the central web
// ApiClient (CSRF, 401 auto-logout, traceparent, observability) and
// re-exports the types the web importers rely on, so no call sites change.
import { createWorkOrdersService, type WorkOrdersService } from '@turn-connect/shared';
import { getApiBaseUrl } from '../utils/config';
import { resizeImageFile } from '../utils/imageResizer';
import { webApiClient } from './apiClient';

const sharedWorkOrdersService = createWorkOrdersService(webApiClient, getApiBaseUrl(), {
  // Web callers catch-and-toast errors from list getters — don't soft-fail to [].
  throwOnListErrors: true,
  // Web render URLs have always encodeURIComponent()ed the file key.
  encodeRenderKeys: true,
});

// Web-only behavior layered on top of the shared service:
// - addFile: downscale large images in the browser before upload
// - getRecentWorkOrdersByVin: web includes soft-deleted WOs and enriches
//   active/recent results with full service details (mobile lazy-loads them)
const webOverrides = {
  addFile: async (
    workOrderId: string,
    file: File | Blob,
    metadata?: { service_id?: string; label?: string; service_name?: string; comment?: string; type?: 'photo' | 'document' }
  ): Promise<void> => {
    const fileToUpload = file instanceof File ? await resizeImageFile(file) : file;
    return sharedWorkOrdersService.addFile(workOrderId, fileToUpload, metadata);
  },
  getRecentWorkOrdersByVin: (locationId: string, vin: string, limit: number = 10) =>
    sharedWorkOrdersService.getRecentWorkOrdersByVin(locationId, vin, limit, { includeDeleted: true, enrichDetails: true }),
};

export const workOrdersService: WorkOrdersService = new Proxy(sharedWorkOrdersService, {
  get(target, prop) {
    if (Object.prototype.hasOwnProperty.call(webOverrides, prop)) {
      return webOverrides[prop as keyof typeof webOverrides];
    }
    const value = Reflect.get(target, prop, target);
    return typeof value === 'function' ? value.bind(target) : value;
  },
});

// Re-export the types historically defined in this module so the ~45 web
// importers keep working unchanged.
export type {
  VehicleInfo,
  WorkOrderTag,
  WorkOrderTypeInfo,
  WorkOrderTypesResponse,
  CreateWorkOrderPayload,
  UpdateWorkOrderPayload,
  WorkOrderSummary,
  WorkOrderDepartmentView,
  WorkOrderSectionView,
  WorkflowStep,
  WorkOrderDetail,
  WoFile,
  Workflow,
  ServiceCompletionResult,
} from '@turn-connect/shared';
